/**
 * Локаль как состояние React: перерисовка на смене языка и `<html lang dir>`.
 *
 * Каталог и хранение выбора живут в `./index.ts`; здесь только обвязка. Разрез
 * ровно по границе React: приёмки и модули без разметки ходят туда, экраны —
 * сюда.
 *
 * `<html lang>` и `<html dir>` ставятся отсюда и только отсюда. От `lang`
 * зависят переносы, форма кавычек и чтение вслух; от `dir` — вся раскладка
 * арабского кабинета (B4). В `index.html` стоит `lang="ru"` без `dir`: это
 * дефолт РФ, и он остаётся правдой до первой отрисовки.
 */

import {
  Fragment,
  createElement,
  useEffect,
  useSyncExternalStore,
  type ReactNode,
} from 'react';

import { market, parseLocale, type RichValues } from '@shared/src/i18n';

import {
  DIRECTION,
  chooseLocale,
  initialLocale,
  locale,
  onLocaleChange,
  t,
  tParts,
} from './index';

/** Активная локаль как состояние React: смена языка перерисовывает экран. */
export function useLocale() {
  return useSyncExternalStore(onLocaleChange, locale, locale);
}

/**
 * `t()`, подписанный на локаль. Возвращается та же функция — перерисовку даёт
 * подписка, а не новая ссылка.
 */
export function useT(): typeof t {
  useLocale();
  return t;
}

/**
 * Строка со вставками разметки: `Условия — в <a>оферте</a>` собирается одним
 * куском, а не склейкой из трёх.
 *
 * Склейка — это не стилистика. В арабском порядок слов другой, и переводчик
 * обязан иметь право переставить ссылку внутри фразы; три отдельные строки
 * такого права не дают, а дают три подписи, которые на втором языке стоят не в
 * том порядке.
 */
export function tx(key: string, values: RichValues<ReactNode>): ReactNode {
  return tParts<ReactNode>(key, values).map((part, index) =>
    createElement(Fragment, { key: index }, part),
  );
}

/**
 * Оболочка кабинета: ставит начальный язык, держит `<html lang dir>` и
 * пересобирает дерево при смене языка.
 *
 * Начальный язык уже стоит к первой отрисовке, если он совпадает с дефолтом
 * рынка (в РФ — всегда): каталог этой локали синхронный. Для остальных первый
 * кадр идёт на языке рынка, а замена приезжает следующим — это честнее пустого
 * экрана в ожидании куска сборки.
 *
 * ─── Почему `key`, а не просто перерисовка ──────────────────────────────────
 *
 * Оболочка подписана на локаль, но одной подписки мало: `children` — это один
 * и тот же элемент, и React на нём сворачивает обход, не заходя в поддерево.
 * Экран, который зовёт `t()` без `useT()` — а таких большинство, пока перенос
 * строк не закончен (B5), — остался бы на прежнем языке до первой своей
 * перерисовки по другой причине. Получился бы кабинет, где половина подписей
 * английская, а половина русская, и понять, какая половина, нельзя.
 *
 * `key` заставляет React собрать дерево заново, то есть перечитать каждую
 * строку. Платим состоянием экранов и повторным запросом сессии — но смена
 * языка это событие уровня страницы, а не действие внутри экрана, и человек
 * ждёт от неё именно полной замены. В РФ этот путь не проходится ни разу:
 * язык там один, и `key` не меняется никогда.
 */
export function I18nProvider({ children }: { children: ReactNode }) {
  const current = useLocale();

  useEffect(() => {
    const want = initialLocale();
    if (want !== locale()) void chooseLocale(want);
  }, []);

  useEffect(() => {
    document.documentElement.lang = current;
    document.documentElement.dir = DIRECTION[current];
  }, [current]);

  return createElement(Fragment, { key: current }, children);
}

/**
 * Язык вошедшего, посчитанный сервером.
 *
 * `Account.locale` — источник правды, копия в браузере лишь ускоряет первый
 * кадр; поэтому копия обновляется отсюда, а не наоборот. Неизвестное значение
 * (`de`, пустая строка, мусор из старой записи) молча уходит в дефолт рынка:
 * показать кабинет на языке рынка можно, показать его без строк — нет.
 *
 * ─── Почему «сессии ещё нет» — это не «язык рынка» ──────────────────────────
 *
 * Первая редакция трактовала отсутствие сессии как повод поставить язык рынка,
 * и в паре со сбросом дерева по `key` это дало бесконечный круг: смена языка
 * пересобирает дерево → сессия снова «проверяется» → значения нет → ставим
 * язык рынка → дерево пересобирается опять → сессия приходит на своём языке →
 * ставим его → и так далее. На одном заходе кабинет успевал сходить в API
 * 142 раза, а экран оставался пустым.
 *
 * Правило простое и верное само по себе, без всякого `key`: **отсутствие
 * значения — это не значение**. Пока сессии нет, язык уже выбран
 * (`initialLocale()` — последний выбор человека или язык рынка), и трогать его
 * нечем и незачем.
 */
export function useAccountLocale(value: string | null | undefined): void {
  useEffect(() => {
    if (!value) return;
    const want = parseLocale(value) ?? market().defaultLocale;
    if (want !== locale()) void chooseLocale(want);
  }, [value]);
}
